import React from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import RangeTouch from 'rangetouch';
import { setAltitudeByYear } from '../../store/actions';
import heatlineUrl from '../../lib/img/heat.svg';

const TimelineWrapper = styled.div`
  position: relative;
  cursor: pointer;
  pointer-events: all;
  flex: 1;
  line-height: 1.15;
  margin: 1px 40px 1px 10px;
  background: transparent url(${heatlineUrl}) 50% / 100% 100% no-repeat;
  font-family: "Source Sans Pro", sans-serif;
  font-size: 0.875rem;
  letter-spacing: 0.075em;
  background-size: contain;
  padding: 5px;

  @media only screen and (max-width: 600px){
    font-size: 12px;
  }

  @media only screen and (max-height: 479px),
    only screen and (max-width: 399px) {
      background: none;
      margin: 0 5px;
  }
`;

const YearSpansWrapper = styled.div`
  position: relative;
  cursor: pointer;
  pointer-events: all;
  flex: 1;
  display: flex;
  justify-content: space-between;
  margin-bottom: 0rem;

  @media only screen and (max-width: 399px) {
    font-size: 0.6875rem;
  }

  & > * {
    position: relative;
    text-shadow: 0 0 0.4em black, 0 0 2em black;
    text-align: center;

    &:after {
      content: "";
      position: absolute;
      background-color: rgba(0,0,0,0.8);
      width: 2px;
      height: 1px;
      left: 50%;
      margin-top: 49px;
    }

    &:first-child,
    &:last-child {
      &:after {
        content: none;
      }
    }

    &:last-child {
      width: 2em;
      position: relative;

      span {
        position: absolute;
        right: 0;
        top: 0;
        width: 10vw;
        line-height: 1;
        text-align: right;
      }
    }
  }
`;

const Slider = styled.input`
  width: calc(100% - 16px);
  height: 2.5rem;
  margin: 13px 8px 0 0;
  cursor: pointer;
  background-color: transparent;
  display: block;
  border-right: 1px solid #fff;
  background-image: linear-gradient(
    90deg,
    rgba(255,38,0,0) 1%,
    rgba(255,38,0,0.55) 100%
  );
  background-size: 20% 100%;
  background-repeat: no-repeat;
  background-position: right;


  @media only screen and (max-height: 479px),
    only screen and (max-width: 399px) {
      margin-top: 0;
      border-right: 0;
      height: 1.5rem;
  }

  &,
  &::-webkit-slider-runnable-track,
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    outline: 0;
  }

  &::-moz-range-track,
  &::-moz-range-thumb {
    appearance: none;
    outline: 0;
  }

  &::-webkit-slider-runnable-track {
    height: 1px;
    background-color: white;
  }

  &::-moz-range-track {
    height: 1px;
    box-sizing: border-box;
    background-color: white;
  }

  &::-ms-track {
    height: 1px;
    background-color: white;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    margin-top: -10px;
    box-sizing: border-box;
    border: none;
     width: 20px;
    height: 20px;
    border-radius: 100%;
    background: #ff5f00;
    border: 3px solid white;
    box-shadow: 0 0 20px black;
    position: relative;
    z-index: 1;
    background: red;
  }

  &::-moz-range-thumb {
    -moz-appearance: none;
    margin-top: -10px;
    box-sizing: border-box;
    border: none;
    width: 20px;
    height: 20px;
    border-radius: 100%;
    background: #ff5f00;
    border: 3px solid white;
    box-shadow: 0 0 20px black;
    position: relative;
    z-index: 1;
  }

  &::-ms-thumb {
    -webkit-appearance: none;
    margin-top: -10px;
    box-sizing: border-box;
    border: none;
    width: 20px;
    height: 20px;
    border-radius: 100%;
    background: #ff5f00;
    border: 3px solid white;
    box-shadow: 0 0 20px black;
    position: relative;
    z-index: 1;
  }
`;

const CelsiusWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: -35px;
  bottom: 0.75rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  pointer-events: none;
  font-family: inherit;
  font-size: 0.875em;
  color: #ffd6b6;

  & > * {
    display: block;
  }

  @media only screen and (max-height: 479px),
    only screen and (max-width: 399px) {
      display: none;
  }
`;

/**
* Timeline React Component.
* @return {jsx} jsx.
*/
const Timeline = ({ year, updateYear }) => {
  const inputRef = React.createRef();

  React.useEffect(() => {
    const range = new RangeTouch(inputRef.current, { addCSS: false });

    inputRef.current.addEventListener('input', updateYear);

    return () => {
      range.destroy();
    };
  }, [inputRef, updateYear]);

  return (
    <TimelineWrapper>
      <YearSpansWrapper>
        <span>2000</span>
        <span>2050</span>
        <span>2100</span>
        <span>2150</span>
        <span>2200</span>
        <span>2250</span>
        <span>2300</span>
        <span><span>Ice caps melt</span></span>
      </YearSpansWrapper>

      <Slider
        id="timeline"
        aria-label="timeline"
        ref={inputRef}
        type="range"
        value={year}
        onChange={updateYear}
        min={2000}
        max={2350}
        step="1"
      />

      <CelsiusWrapper>
        <span>+8C°</span>
        <span>+4C°</span>
        <span>+0C°</span>
      </CelsiusWrapper>
    </TimelineWrapper>
  );
};

Timeline.propTypes = {
  year: PropTypes.number.isRequired,
  updateYear: PropTypes.func.isRequired,
};

export default connect(
    ({ altitudes }) => ({
      year: altitudes.year,
    }),

    (dispatch) => ({
      updateYear: ({ target }) => dispatch(
          setAltitudeByYear(parseInt(target.value, 10)),
      ),
    }),
)(Timeline);
